import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type ReactElement } from "react";
import { errorMessage } from "@/app/api";
import { marginText, perBilled, periodLabel, REPORT_DIMS, REPORT_SPAN_DAYS, reports, type ReportDim, type ReportFilter, type ReportPeriod, type ReportRow, type ReportSide, type ReportTotal } from "@/app/billing";
import { accounts, channels, customers, suppliers } from "@/app/master";
import { ClearFilters } from "@/app/ClearFilters";
import { Pager } from "@/app/Pager";
import { loadingOf, nameOptions, nameOf, useList, useNames } from "@/app/queries";
import { ShowPicker } from "@/app/ShowPicker";
import { ToolIcon } from "@/app/toolIcons";
import { useShown } from "@/app/useShown";
import { NameId } from "@/app/NameId";
import { totalOf, useOffsetPage } from "@/app/usePage";
import { useRange } from "@/app/useRange";
import { Alert, Button, Combobox, DateRange, EmptyState, FILTER_W, formatMoney, Money, PageHeader, Select, Table, TableWrap, Th, Toolbar, type ColWidth } from "@/ui";

/** 营收数据按账号和历史售价拆行，带成本、成本单价、利润、毛利率与按币种的合计，可导出 Excel（D107、D195、D197）。 */
export function CustomerReport() { return <BillingReport side="customers" />; }

/** 成本报表按通道和历史采购价拆行（D107）。 */
export function SupplierReport() { return <BillingReport side="suppliers" />; }

const DESCRIPTION: Record<ReportSide, string> = {
  customers: "按网关接收时间归集（北京时间），提交含路由失败与上游拒绝，数量为段数。成本含全部上游尝试（含重试）；未定价的行按基准币种、不进合计。",
  suppliers: "按成功提交到通道的时间归集（北京时间），重试分别计数，数量以上游实际段数为准。",
};

const DEFAULT_RANGE = { preset: "7d" } as const;

/** 表格的列键：时间与四个维度，三个数量，单价，金额类（成本报表只有 amount）。 */
type ReportColumn = "time" | "entity" | "owner" | "mcc" | "mnc" | "submitted" | "delivered" | "billed" | "price" | "amount" | "cost" | "unitCost" | "profit" | "margin";
const DIM_COLUMNS: ReportColumn[] = ["time", "entity", "owner", "mcc", "mnc"];

/** 一侧报表的列，按表格里的顺序，w 是固定列宽，显示的最后一列不给宽度、吸收剩余（#541，决策 D199）；时间列固定，其余可在“显示列”里隐藏。 */
function reportColumns(customer: boolean, entity: string, owner: string, grouped: boolean): { key: ReportColumn; label: string; w: ColWidth; numeric?: boolean }[] {
  const money = (key: ReportColumn, label: string) => ({ key, label, w: "money" as const, numeric: true });
  return [
    { key: "time", label: "时间", w: 160 }, { key: "entity", label: entity, w: "name" }, { key: "owner", label: owner, w: 180 },
    { key: "mcc", label: "MCC", w: 88 }, { key: "mnc", label: "MNC", w: 96 },
    { key: "submitted", label: "提交", w: "num", numeric: true }, { key: "delivered", label: "送达", w: "num", numeric: true }, { key: "billed", label: "计费", w: "num", numeric: true },
    money("price", grouped ? "均价" : "单价"),
    ...(customer ? [money("amount", "收入"), money("cost", "成本"), money("unitCost", "成本单价"), money("profit", "利润"), { key: "margin" as const, label: "毛利率", w: "num" as const, numeric: true }] : [money("amount", "金额")]),
  ];
}
const PERIODS: Array<{ value: ReportPeriod; label: string }> = [{ value: "10m", label: "10 分钟" }, { value: "hour", label: "按小时" }, { value: "day", label: "按日" }, { value: "month", label: "按月" }];

function BillingReport({ side }: { side: ReportSide }) {
  const customer = side === "customers";
  const { range, setRange, span, refresh } = useRange(DEFAULT_RANGE);
  const [period, setPeriod] = useState<ReportPeriod>("day");
  const [group, setGroup] = useState<ReportDim[]>([]);
  const [ownerId, setOwnerId] = useState("");
  const [entityId, setEntityId] = useState("");
  const allColumns = reportColumns(customer, customer ? "账号" : "通道", customer ? "客户" : "供应商", group.length > 0);
  const columnKeys = allColumns.map((c) => c.key);
  const [shownColumns, setColumns] = useShown("columns", `report-${side}`, columnKeys, columnKeys);
  const columns = allColumns.filter((c) => c.key === "time" || shownColumns.includes(c.key));
  const shown = columns.map((c) => c.key);
  // 有任何条件（时间范围、周期、维度、聚合不是缺省）才显示清除筛选
  const filtered = !!(ownerId || entityId || group.length) || period !== "day" || !("preset" in range && range.preset === DEFAULT_RANGE.preset);
  const clear = () => { setRange(DEFAULT_RANGE); setPeriod("day"); setGroup([]); setOwnerId(""); setEntityId(""); };
  const accountList = useList("accounts", accounts);
  const channelList = useList("channels", channels);
  const customerList = useList("customers", customers);
  const supplierList = useList("suppliers", suppliers);
  const names = useNames();
  const qc = useQueryClient();
  const filter: ReportFilter = { from: span?.from.toISOString() ?? "", to: span?.to.toISOString() ?? "", period, group, entityId, ownerId };
  const pager = useOffsetPage(`report-${side}`, [filter.from, filter.to, period, group, entityId, ownerId]);
  const millis = span ? span.to.getTime() - span.from.getTime() : 0;
  const valid = millis > 0 && millis <= REPORT_SPAN_DAYS[period] * 86_400_000;
  const list = useQuery({ queryKey: ["reports", side, filter, pager.query], queryFn: () => reports.list(side, filter, pager.query), enabled: valid, placeholderData: keepPreviousData });
  const total = useQuery({ queryKey: ["reports", side, filter, "count"], queryFn: () => reports.count(side, filter), enabled: valid }); // 行数（决策 D182）
  const sums = useQuery({ queryKey: ["reports", side, filter.from, filter.to, entityId, ownerId, "total"], queryFn: () => reports.total(side, filter), enabled: valid && customer }); // 合计（D195）
  const exporting = useMutation({ mutationFn: () => reports.exportRevenue(filter) });
  const paged = { ...pager.view(valid ? list.data ?? [] : []), ...totalOf(valid ? total.data : undefined) };
  const entity = customer ? "账号" : "通道";
  const owner = customer ? "客户" : "供应商";
  const ownerRows = customer ? customerList.data : supplierList.data;
  const entityRows = customer ? accountList.data?.filter((a) => !ownerId || String(a.customer_id) === ownerId) : channelList.data?.filter((c) => !ownerId || String(c.supplier_id) === ownerId);
  // 聚合同通道健康页（D186）：维度下拉多选，选中的合起来作为聚合键，都不选显示“不聚合”
  const dimOptions = REPORT_DIMS.map((d) => ({ value: d, label: d === "entity" ? entity : d === "mcc" ? "MCC" : "MCC+MNC" }));
  // 相对范围（近 7 天之类）随刷新往前走，绝对范围原样重取
  const reload = () => { refresh(); void qc.invalidateQueries({ queryKey: ["reports", side] }); };
  return <>
    <PageHeader title={customer ? "营收数据" : "成本报表"} description={DESCRIPTION[side]} />
    {!valid && <Alert tone="danger" title="时间范围无效">请选择先后有序的时间范围：10 分钟最多 7 天，按小时最多 92 天，按日、按月最多 366 天。</Alert>}
    {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
    {exporting.error && <Alert tone="danger" title="导出失败">{errorMessage(exporting.error)}</Alert>}
    <Toolbar actions={<>
      <ShowPicker label="显示列" icon={<ToolIcon name="columns-3" />} heading="显示的列" options={allColumns.filter((c) => c.key !== "time")} shown={shownColumns} onChange={setColumns} />
      {filtered && <ClearFilters onClear={clear} />}
      <Button size="sm" disabled={!valid || list.isFetching} onClick={reload}>刷新</Button>
    </>}
      right={customer && <Button size="sm" disabled={!valid || exporting.isPending} onClick={() => exporting.mutate()}>{exporting.isPending ? "导出中…" : "导出 Excel"}</Button>}>
      <DateRange size="sm" value={range} onChange={setRange} />
      <Select size="sm" width={FILTER_W.select} ariaLabel="统计周期" value={period} onChange={(v) => setPeriod(v as ReportPeriod)} options={PERIODS} />
      <Combobox size="sm" ariaLabel={owner} width={FILTER_W.select} searchPlaceholder={`搜索${owner}名称或 ID`} value={ownerId} onChange={(id) => { setOwnerId(id); setEntityId(""); }} options={[{ value: "", label: `全部${owner}` }, ...nameOptions<{ id: number; name: string }>(ownerRows)]} />
      <Combobox size="sm" ariaLabel={entity} width={FILTER_W.select} searchPlaceholder={`搜索${entity}名称或 ID`} value={entityId} onChange={setEntityId} options={[{ value: "", label: `全部${entity}` }, ...nameOptions(entityRows?.map((e) => ({ id: e.id, name: e.name, system_id: "system_id" in e ? e.system_id : undefined })))]} />
      {/* 聚合放在筛选控件的最后（用户 2026-10-14 要求） */}
      <Combobox multiple searchable={false} size="sm" width={FILTER_W.select} ariaLabel="聚合" placeholder="不聚合" options={dimOptions} value={group} onChange={(v) => setGroup(REPORT_DIMS.filter((d) => v.includes(d)))} />
    </Toolbar>
    <TableWrap loading={valid && loadingOf(list)}>{paged.rows.length ? <>
      <Table dense><thead><tr>{columns.map((c, i) => <Th key={c.key} numeric={c.numeric} w={i === columns.length - 1 ? undefined : c.w}>{c.label}</Th>)}</tr></thead>
        <tbody>{paged.rows.map((r) => <tr key={`${r.period}-${r.mcc}-${r.mnc}-${r.entity_id}-${r.owner_id}-${r.unit_price}-${r.currency}-${r.amount === null}`}>
          {shown.map((k) => <Fragment key={k}>{rowCells(r, { customer, period, grouped: group.length > 0, entity, owner, entityName: (id) => (customer ? names.account(id) : nameOf(channelList.data, id)), ownerName: (id) => (customer ? names.customer(id) : nameOf(supplierList.data, id)) })[k]}</Fragment>)}
        </tr>)}</tbody>
        {customer && !!sums.data?.length && <TotalRows totals={sums.data} shown={shown} />}
      </Table><Pager {...paged} />
    </> : (!valid || !list.isPending) && <EmptyState title="没有报表数据" />}</TableWrap>
  </>;
}

/** 一行的各列单元格，按列键取；聚合行涉及多个时维度列写个数（同通道健康页）；报表按历史所有者快照拆行，所有者单独成列，区分同一实体换过所有者的两行（D107、#325）。 */
function rowCells(r: ReportRow, o: { customer: boolean; period: ReportPeriod; grouped: boolean; entity: string; owner: string; entityName: (id: number) => string; ownerName: (id: number) => string }): Partial<Record<ReportColumn, ReactElement>> {
  return {
    time: <td className="ax-mono">{periodLabel(r.period, o.period)}</td>,
    entity: <td>{r.entities > 1 ? `${r.entities} 个${o.entity}` : <NameId name={o.entityName(r.entity_id)} id={r.entity_id} />}</td>,
    owner: <td>{r.owners > 1 ? `${r.owners} 个${o.owner}` : <NameId name={o.ownerName(r.owner_id)} id={r.owner_id} />}</td>,
    mcc: <td className="ax-mono">{r.mccs > 1 ? `${r.mccs} 个 MCC` : r.mcc === "XXX" ? "未知" : r.mcc}</td>,
    mnc: <td className="ax-mono">{r.operators > 1 ? `${r.operators} 个${r.mccs > 1 ? "运营商" : " MNC"}` : r.mnc || "未知"}</td>,
    ...countCells(r),
    price: <PriceCell row={r} grouped={o.grouped} />,
    ...moneyCells(r, o.customer),
  };
}

/** 提交、送达、计费三列。 */
function countCells(r: Pick<ReportRow, "submitted" | "delivered" | "billed">) {
  return { submitted: <td className="ax-num">{r.submitted}</td>, delivered: <td className="ax-num">{r.delivered}</td>, billed: <td className="ax-num">{r.billed}</td> };
}

/** 单价列：明细是历史单价；聚合时一行可能混着几种单价，显示均价（收入 ÷ 计费，D197）。未定价的行明确写出，不冒充免费。 */
function PriceCell({ row, grouped }: { row: ReportRow; grouped: boolean }) {
  const price = grouped ? perBilled(row.amount, row.billed) : row.unit_price;
  const text = price !== null ? <span className="ax-money">{formatMoney(price, row.currency, 6)}</span> : row.amount === null ? "未定价" : "—";
  return <td className="ax-num">{text}</td>;
}

/** 金额列：营收数据是收入、成本、成本单价、利润、毛利率，成本报表只有金额。 */
function moneyCells(row: Pick<ReportRow, "amount" | "cost" | "profit" | "currency" | "billed">, customer: boolean): Partial<Record<ReportColumn, ReactElement>> {
  const amount = <td className="ax-num">{row.amount === null ? "—" : <Money micro={row.amount} currency={row.currency} />}</td>;
  if (!customer) return { amount };
  const unitCost = perBilled(row.cost, row.billed);
  return {
    amount,
    cost: <MoneyCell micro={row.cost} currency={row.currency} />,
    unitCost: <td className="ax-num">{unitCost === null ? "—" : <span className="ax-money">{formatMoney(unitCost, row.currency === "XXX" ? undefined : row.currency, 6)}</span>}</td>,
    profit: <MoneyCell micro={row.profit} currency={row.currency} />,
    margin: <td className="ax-num">{marginText(row.profit, row.amount) ?? "—"}</td>,
  };
}

/** 成本、利润单元格：币种未知（XXX）的行没有售价，金额是基准币种，不标币种（同概览，D195）。 */
function MoneyCell({ micro, currency }: { micro: number | null; currency: string }) {
  return <td className="ax-num">{micro === null ? "—" : <Money micro={micro} currency={currency === "XXX" ? undefined : currency} />}</td>;
}

/** 合计行：全部筛选结果按币种各一行，不随翻页变化（D195）；未定价的行不进合计（D197）。标签跨显示的时间与维度列，单价列留空。 */
function TotalRows({ totals, shown }: { totals: ReportTotal[]; shown: ReportColumn[] }) {
  const dims = shown.filter((k) => DIM_COLUMNS.includes(k)).length;
  return <tfoot>{totals.map((t) => {
    const cells: Partial<Record<ReportColumn, ReactElement>> = { ...countCells(t), price: <td />, ...moneyCells(t, true) };
    return <tr key={t.currency}>
      <td colSpan={dims}>合计 {t.currency}</td>
      {shown.filter((k) => !DIM_COLUMNS.includes(k)).map((k) => <Fragment key={k}>{cells[k]}</Fragment>)}
    </tr>;
  })}</tfoot>;
}
